import type { DocumentNode, GraphQLFormattedError } from 'graphql';
import type { IncomingMessage } from 'node:http';

import { getDomain, MfeNames } from '@/utils/domains.js';
import { getGraphQLPath, resolveGraphQLAppName } from '@/utils/graphql-path';

export type InProcessGraphQLInput = {
    document: DocumentNode;
    variables?: Record<string, unknown>;
    operationName?: string;
    /** Exactly the headers the HTTP transport would have sent (see `createSsrPrefetchRequestHeaders`). */
    headers: Record<string, string>;
    /** The page request being rendered; never handed to resolvers, only used to derive the synthetic request. */
    req: IncomingMessage;
};

export type InProcessGraphQLResult<TData> = {
    data: TData | null;
    errors?: readonly GraphQLFormattedError[];
    status: number;
};

/**
 * Runs one GraphQL operation against the registering app's own Apollo Server, in this process.
 * By contract it never throws: its own failures come back as `status: 500` with one error.
 */
export type InProcessGraphQLExecutor = <TData>(
    input: InProcessGraphQLInput,
) => Promise<InProcessGraphQLResult<TData>>;

type InProcessGraphQLRegistry = {
    /** Executors keyed by the server-side GraphQL endpoint their owner serves. */
    owners: Map<string, InProcessGraphQLExecutor>;
    /** The endpoint each owner registered under, so a reset removes exactly that entry. */
    endpointByOwner: Map<MfeNames, string>;
};

// Next compiles API routes and pages as separate module registries, so a module-level variable
// would be a different object in each; globalThis is the one shared scope. The key carries the
// contract version: federated remotes call these executors through their own bundled copy of this
// module, so a bundle built against another registry shape or target rule must see no executor
// and stay on HTTP. v3 holds several owners and resolves path aliases on both sides.
const REGISTRY_KEY = Symbol.for('@tn/foundation/server/in-process-graphql/v3');

const getRegistry = (): InProcessGraphQLRegistry => {
    const cache = globalThis as Record<symbol, unknown>;
    let registry = cache[REGISTRY_KEY] as InProcessGraphQLRegistry | undefined;
    if (!registry) {
        registry = { owners: new Map(), endpointByOwner: new Map() };
        cache[REGISTRY_KEY] = registry;
    }
    return registry;
};

// The URL the HTTP transport would request server-side: the aliased app's domain plus the path, as
// `getGraphQLRequestOrigin` builds it. The domain alone is not enough: a shell pod serves more than
// one GraphQL route (app-shell, user-profiles) under its own domain.
const getServerGraphQLEndpoint = (appName: MfeNames): string | null => {
    const domain = getDomain(resolveGraphQLAppName(appName), undefined, true);
    return domain ? `${domain}${getGraphQLPath(appName)}` : null;
};

/** Registers `executor` for the endpoint `ownerAppName` serves; other owners are kept. */
export const registerInProcessGraphQL = (
    executor: InProcessGraphQLExecutor,
    ownerAppName: MfeNames,
): void => {
    const endpoint = getServerGraphQLEndpoint(ownerAppName);
    if (!endpoint) return;
    const registry = getRegistry();
    registry.owners.set(endpoint, executor);
    registry.endpointByOwner.set(ownerAppName, endpoint);
};

/**
 * True when `appName` resolves to a registered owner's GraphQL endpoint. The comparison is against
 * the owners, never against this bundle's own app name: a federated remote rendering inside the
 * shell pod shares this registry and stays on HTTP unless its GraphQL owner is aliased to a
 * registered owner (hubs and ptv-appshell resolve to app-shell).
 */
export const isInProcessTarget = (appName: MfeNames): boolean => {
    const endpoint = getServerGraphQLEndpoint(appName);
    return !!endpoint && getRegistry().owners.has(endpoint);
};

/** The executor of the owner `appName` resolves to, otherwise null. */
export const getInProcessExecutorFor = (appName: MfeNames): InProcessGraphQLExecutor | null => {
    const endpoint = getServerGraphQLEndpoint(appName);
    return (endpoint && getRegistry().owners.get(endpoint)) || null;
};

/** Clears one owner when given, every owner otherwise. */
export const _resetInProcessGraphQLForTests = (ownerAppName?: MfeNames): void => {
    const registry = getRegistry();
    if (!ownerAppName) {
        registry.owners.clear();
        registry.endpointByOwner.clear();
        return;
    }
    const endpoint = registry.endpointByOwner.get(ownerAppName);
    if (endpoint) registry.owners.delete(endpoint);
    registry.endpointByOwner.delete(ownerAppName);
};
